import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StepsNotClientTypes } from '../../shared/enums';
import { EncryptationFlagr } from './encryptation-flagr';
import { environment } from '../../../environments/environment';
import { DataNotClientService } from '../data-not-client.service';
import { Store } from '@ngrx/store';
import { OnboardingProductsStore } from 'src/app/store/store';
import { UiActions } from 'src/app/store/actions';

interface FlagrResponse {
  flagID: number;
  flagKey: string;
  variantAttachment: {
    toggles: string;
  };
  variantID: number;
  variantKey: 'on' | 'off';
}

@Injectable({ providedIn: 'root' })
export class FlagrService {
  private encryptationFlagr = new EncryptationFlagr();

  constructor(
    private http: HttpClient,
    private dataNotClientService: DataNotClientService,
    private store: Store<OnboardingProductsStore.AppState>
  ) { }

  async flagrEvaluation(): Promise<void> {
    const checkFlagr = await this.canShowFaceID();
    let nextStep: StepsNotClientTypes = StepsNotClientTypes.STEP_TERMS_CONDITIONS;
    if (!checkFlagr) {
      if (!this.dataNotClientService.getIsRetake()) {
        nextStep = StepsNotClientTypes.STEP_FACE_RECOGNITION;
      }
    }
    this.store.dispatch(UiActions.Navigate({ step: nextStep }));
  }

  async canShowFaceID(): Promise<boolean> {
    const dniNumber = this.dataNotClientService.getIndentificationNumber();
    const dniType = this.dataNotClientService.getIdentificationType();
    let resp = false;
    const payload = await this.call(dniType, dniNumber);
    if (payload && payload.variantKey === 'on') {
      resp = true;
    }

    return resp;
  }

  private async call(docType: string, dni: string): Promise<FlagrResponse> {
    const { url, flagKey } = environment.flagr;
    const body = {
      flag: {
        entityID: this.encryptationFlagr.generateRandomKey(),
        entityContext: {
          docType,
          dni,
          checkDigit: dni.slice(-1),
          checkTwoDigit: dni.slice(-2)
        },
        enableDebug: false,
        flagKey
      }
    };
    const cipheredBody = this.encryptationFlagr.encryptAllValuesFromObject(body);
    const signature = this.encryptationFlagr.createSignature(cipheredBody);
    const cipheredNewKey = this.encryptationFlagr.keyEncrypt;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-session-key': cipheredNewKey,
      'x-signature': signature
    });
    let resp: FlagrResponse;
    try {
      const { payload } = await this.http.post<{ payload: string }>(url, cipheredBody, { headers }).toPromise();
      resp = this.decryptPayload(payload);
    } catch (e) {
      this.store.dispatch(UiActions.Navigate({ step: StepsNotClientTypes.STEP_SERVICE_ERROR }));
    }

    return resp;
  }

  private decryptPayload(encryptedPayload: string): FlagrResponse {
    let resp: FlagrResponse;
    if (encryptedPayload && encryptedPayload > '') {
      const strResp = this.encryptationFlagr.aesDecrypt(encryptedPayload);
      resp = JSON.parse(strResp).payload;
    }
    return resp;
  }
}
